import React, { useEffect, useRef, useState } from 'react';
import {
  Animated,
  Pressable,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { FontFamily } from '../../../constants/fonts';
import { useTheme } from '../../../theme';


export interface ChartItem {
  id?: number | string;
  label: string;

  value?: number;
  valueLabel?: string;

  percentage?: number;

  topLabel?: string;
  bottomLabel?: string;

  clickable?: boolean;

  segments?: {
    label: string;
    value: number;
    color: string;
  }[];

  /** For stacked charts: number of open items (shown in tooltip) */
  openCount?: number;

  /** For stacked charts: number of closed items (shown in tooltip) */
  closedCount?: number;
}

interface Props {
  title: string;
  subtitle?: string;

  data: ChartItem[];

  chartType?: 'single' | 'stacked';

  metadataPosition?: 'top' | 'bottom' | 'inside';

  toggleValue?: string;
  toggleOptions?: string[];
  onToggleChange?: (value: any) => void;

  legend?: {
    label: string;
    color: string;
  }[];
  legendPosition?: 'bottom' | 'topRight';

  showBackButton?: boolean;
  onBack?: () => void;

  onBarPress?: (item: ChartItem) => void;

  minHeight?: number;

  /**
   * When true the chart area is replaced with an animated shimmer placeholder
   * while keeping the card chrome (title / toggle) visible so nothing jumps
   * once the API data arrives.
   */
  loading?: boolean;

  /** Number of placeholder bars rendered while loading. */
  skeletonBarCount?: number;
  /** Optional error message to display inside the card when the request failed. */
  error?: string | null;
  /** Called when the user taps the Retry button shown in the error state. */
  onRetry?: () => void;
  /**
   * When true and the chart has no data, shows "Try expanding your date range or
   * removing some filters." instead of the generic empty-state message.
   */
  hasActiveFilters?: boolean;
}

/**
 * Shimmering bar placeholders shown inside the chart area while data loads.
 * Kept inside this file so every consumer gets the skeleton simply by passing
 * `loading`.
 */
const BarChartSkeleton: React.FC<{ height: number; barCount: number }> = ({ height, barCount }) => {
  const shimmer = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(shimmer, { toValue: 1, duration: 900, useNativeDriver: true }),
        Animated.timing(shimmer, { toValue: 0, duration: 900, useNativeDriver: true }),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [shimmer]);

  const opacity = shimmer.interpolate({ inputRange: [0, 1], outputRange: [0.35, 0.75] });

  // Deterministic, varied heights so the placeholder reads as a chart.
  const BAR_HEIGHTS = [72, 54, 88, 42, 66, 34, 78, 48];

  return (
    <View style={[styles.chart, { height }]} accessibilityLabel="Loading chart" accessible>
      <View style={styles.barsRow}>
        {Array.from({ length: barCount }).map((_, index) => (
          <View key={`chart-skeleton-bar-${index}`} style={styles.col}>
            <Animated.View
              style={[
                styles.skeletonBar,
                { height: `${BAR_HEIGHTS[index % BAR_HEIGHTS.length]}%`, opacity },
              ]}
            />
          </View>
        ))}
      </View>
      <View style={styles.labelsRow}>
        {Array.from({ length: barCount }).map((_, index) => (
          <View key={`chart-skeleton-label-${index}`} style={styles.col}>
            <Animated.View style={[styles.skeletonLabel, { opacity }]} />
          </View>
        ))}
      </View>
    </View>
  );
};

const VerticalBarChartCard: React.FC<Props> = ({

  title,
  subtitle,
  data,
  chartType = 'single',
  metadataPosition = 'top',
  toggleValue,
  toggleOptions,
  onToggleChange,
  legend,
  legendPosition = 'bottom',
  showBackButton,
  onBack,
  onBarPress,
  minHeight = 230,
  loading = false,
  skeletonBarCount = 6,
  error = null,
  onRetry,
  hasActiveFilters = false,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const maxValue = Math.max(
    ...data.map(item =>

      chartType === 'stacked'
        ? item.segments?.reduce((a, b) => a + b.value, 0) ?? 0
        : item.value ?? 0,
    ),
    1,
  );

  const hasData = data && data.length > 0 && data.some(item => (item.value ?? (item.segments?.reduce((a, b) => a + b.value, 0) ?? 0)) > 0);

  // Tooltip state for hover (web) and press (native) interactions
  const [hovered, setHovered] = useState<null | { index: number; item: ChartItem; segment?: { label: string; value: number; color: string }; autoHide?: boolean }>(null);
  const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number } | null>(null);
  const chartRef = useRef<any>(null);
  const tooltipRef = useRef<any>(null);
  const [tooltipWidth, setTooltipWidth] = useState<number>(0);
  const containerRect = useRef<{ left: number; top: number; width: number; height: number } | null>(null);
  const hideTimer = useRef<number | null>(null);

  const clearHideTimer = () => {
    if (hideTimer.current) {
      // @ts-ignore window exists on web; on native this will be undefined and ignored
      clearTimeout(hideTimer.current as any);
      hideTimer.current = null;
    }
  };

  const handlePressIn = (index: number, item: ChartItem, e?: any) => {
    setHovered({ index, item, autoHide: true });
    clearHideTimer();
    // set tooltip position from native event when available
    if (e && e.nativeEvent) {
      // web: clientX / clientY, native: locationX / locationY
      const ne = e.nativeEvent as any;
      let x = typeof ne.clientX === 'number' ? ne.clientX : typeof ne.locationX === 'number' ? ne.locationX : null;
      let y = typeof ne.clientY === 'number' ? ne.clientY : typeof ne.locationY === 'number' ? ne.locationY : null;
      if (x != null && containerRect.current) {
        x = x - containerRect.current.left;
      }
      if (y != null && containerRect.current) {
        y = y - containerRect.current.top;
      }
      if (x != null && y != null) setTooltipPos({ x, y });
    }

    // auto-hide after 2s for press interactions
    // @ts-ignore
    hideTimer.current = (typeof window !== 'undefined' && (window as any).setTimeout) ? (window as any).setTimeout(() => { setHovered(null); setTooltipPos(null); }, 2000) : null;
  };

  const handleSegmentPressIn = (index: number, item: ChartItem, segment: { label: string; value: number; color: string }, e?: any) => {
    setHovered({ index, item, segment, autoHide: true });
    clearHideTimer();
    if (e && e.nativeEvent) {
      const ne = e.nativeEvent as any;
      let x = typeof ne.clientX === 'number' ? ne.clientX : typeof ne.locationX === 'number' ? ne.locationX : null;
      let y = typeof ne.clientY === 'number' ? ne.clientY : typeof ne.locationY === 'number' ? ne.locationY : null;
      if (x != null && containerRect.current) {
        x = x - containerRect.current.left;
      }
      if (y != null && containerRect.current) {
        y = y - containerRect.current.top;
      }
      if (x != null && y != null) setTooltipPos({ x, y });
    }

    // auto-hide after 2s for press interactions
    // @ts-ignore
    hideTimer.current = (typeof window !== 'undefined' && (window as any).setTimeout) ? (window as any).setTimeout(() => { setHovered(null); setTooltipPos(null); }, 2000) : null;
  };

  useEffect(() => {
    return () => {
      clearHideTimer();
    };
  }, []);

  // Measure container rect for positioning (web uses getBoundingClientRect)
  const handleContainerLayout = () => {
    try {
      if (chartRef.current && typeof (chartRef.current.getBoundingClientRect) === 'function') {
        const r = chartRef.current.getBoundingClientRect();
        containerRect.current = { left: r.left, top: r.top, width: r.width, height: r.height };
      } else if (chartRef.current && typeof chartRef.current.measureInWindow === 'function') {
        // react-native measureInWindow (async callback)
        chartRef.current.measureInWindow((x: number, y: number, w: number, h: number) => {
          containerRect.current = { left: x, top: y, width: w, height: h };
        });
      }
    } catch (err) {
      // ignore
    }
  };

  /**
   * Mouse handlers are only meaningful on react-native-web. They are not part
   * of React Native's `PressableProps` typings, so they're built here and
   * spread onto the element to keep the component type-safe on native.
   */
  const webHoverHandlers = (idx: number, item: ChartItem, segment?: { label: string; value: number; color: string }) => {
    const positionFromEvent = (e: any) => {
      try {
        const ne = e?.nativeEvent as any;
        if (ne && typeof ne.clientX === 'number' && typeof ne.clientY === 'number') {
          if (chartRef.current && containerRect.current === null) {
            handleContainerLayout();
          }
          const left = containerRect.current ? ne.clientX - containerRect.current.left : ne.clientX;
          const top = containerRect.current ? ne.clientY - containerRect.current.top : ne.clientY;
          setTooltipPos({ x: left, y: top });
        }
      } catch (err) {
        // ignore – pointer coords unavailable on this platform
      }
    };

    return {
      onMouseEnter: (e: any) => {
        positionFromEvent(e);
        setHovered({ index: idx, item, ...(segment ? { segment } : {}), autoHide: false });
      },
      onMouseMove: (e: any) => {
        positionFromEvent(e);
        setHovered({ index: idx, item, ...(segment ? { segment } : {}), autoHide: false });
      },
      onMouseLeave: () => {
        setHovered(null);
        setTooltipPos(null);
      },
    };
  };

  // Build tooltip text based on hovered state. For stacked charts we prefer
  // to show the total assigned + a breakdown of each segment (e.g. "117
  // assigned (28 Open, 89 Closed)"). Fall back to percentage/value when
  // needed.
  const tooltipText = (() => {
    if (!hovered) return '';
    const item = hovered.item;
    const segments = item.segments ?? [];

    const total = typeof item.value === 'number'
      ? item.value
      : (segments.length ? segments.reduce((a, b) => a + b.value, 0) : undefined);

    const totalLabel = item.valueLabel ?? (typeof total === 'number' ? String(total) : undefined);

    if (segments.length) {
      const parts = segments.map(s => `${s.value} ${s.label}`);
      const assignedText = totalLabel ? `${totalLabel} assigned` : (typeof total === 'number' ? `${total} assigned` : '0 assigned');
      return `${item.label}: ${assignedText} (${parts.join(', ')})`;
    }

    if (hovered.segment) {
      return `${item.label} — ${hovered.segment.label}: ${hovered.segment.value}`;
    }

    if (item.percentage != null) return `${item.label}: ${item.percentage.toFixed(1)}%`;
    if (totalLabel) return `${item.label}: ${totalLabel}`;
    return `${item.label}: ${item.value ?? 0}`;
  })();

  return (
    <View style={[styles.card, isDark && { backgroundColor: colors.background.surface, borderColor: colors.border.default }]}>
      <View style={styles.header}>
        <View style={styles.leftHeader}>
          {showBackButton && (
            <TouchableOpacity style={[styles.backButton, isDark && { borderColor: colors.border.default }]} onPress={onBack}>
              <Text style={isDark ? { color: colors.text.primary } : undefined}>{'<'}</Text>
            </TouchableOpacity>
          )}

          <View>
            <Text style={[styles.title, isDark && { color: colors.text.primary }]}>{title}</Text>

            {!!subtitle && <Text style={[styles.subtitle, isDark && { color: colors.text.secondary }]}>{subtitle}</Text>}

            {!!legend?.length && legendPosition === 'bottom' && (
              <View style={styles.legendContainer}>
                {legend.map(item => (
                  <View key={item.label} style={styles.legendItem}>
                    <View style={[styles.legendDot, { backgroundColor: item.color }]} />
                    <Text style={isDark ? { color: colors.text.secondary } : undefined}>{item.label}</Text>
                  </View>
                ))}
              </View>
            )}
          </View>
        </View>

        {!!legend?.length && legendPosition === 'topRight' && (
          <View style={styles.legendContainerTopRight}>
            {legend.map(item => (
              <View key={item.label} style={styles.legendItem}>
                <View style={[styles.legendDot, { backgroundColor: item.color }]} />
                <Text style={isDark ? { color: colors.text.secondary } : undefined}>{item.label}</Text>
              </View>
            ))}
          </View>
        )}

        {!!toggleOptions?.length && (
          <View style={[styles.toggle, isDark && { backgroundColor: colors.background.elevated }]}>
            {toggleOptions.map(option => (
              <TouchableOpacity
                key={option}
                style={[
                  styles.toggleOption,
                  toggleValue === option && styles.toggleOptionActive,
                  toggleValue === option && isDark && { backgroundColor: colors.background.surface },
                ]}
                onPress={() => onToggleChange?.(option)}
              >
                <Text style={[
                  styles.toggleOptionText,
                  isDark && { color: colors.text.secondary },
                  toggleValue === option && styles.toggleOptionTextActive,
                  toggleValue === option && isDark && { color: colors.text.primary },
                ]}>
                  {option.charAt(0).toUpperCase() + option.slice(1)}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        )}
      </View>

      {loading ? (
        <BarChartSkeleton height={minHeight} barCount={skeletonBarCount} />
      ) : error ? (
        <View style={[styles.noDataWrap, { minHeight: minHeight }]}>
          <Text style={styles.errorText}>{error}</Text>
          {!!onRetry && (
            <TouchableOpacity onPress={onRetry} style={[styles.retryButton, isDark && { borderColor: colors.brand.primary }]} accessibilityRole="button" accessibilityLabel="Retry">
              <Text style={[styles.retryText, isDark && { color: colors.brand.primary }]}>↻  Retry</Text>
            </TouchableOpacity>
          )}
        </View>
      ) : (
      <View
        ref={chartRef}
        onLayout={handleContainerLayout}
        style={[styles.chart, { minHeight: minHeight }]}
      > 
        {!hasData ? (
          <View style={[styles.noDataWrap, { minHeight: minHeight }]}>
            <Text style={[styles.noDataText, isDark && { color: colors.text.secondary }]}>
              {hasActiveFilters
                ? 'Try expanding your date range or removing some filters.'
                : 'Try expanding your date range or removing some filters.'}
            </Text>
          </View>
        ) : (
          <>
            {/* Tooltip overlay (absolute) - position using tooltipPos when available */}
            {hovered && (
              <View
                ref={tooltipRef}
                onLayout={(ev) => setTooltipWidth(ev.nativeEvent.layout.width)}
                pointerEvents="none"
                style={(() => {
                  // compute left positioning
                  const cw = containerRect.current?.width ?? 0;
                  let leftStyle: any = {};
                  if (tooltipPos && typeof tooltipPos.x === 'number') {
                    // center tooltip over cursor
                    let left = tooltipPos.x - (tooltipWidth / 2 || 0);
                    const min = 6;
                    const max = Math.max(6, cw - (tooltipWidth || 0) - 6);
                    if (!isNaN(left)) {
                      left = Math.min(Math.max(left, min), max);
                    } else {
                      left = min;
                    }
                    leftStyle = { left };
                  } else {
                    // fallback: position based on bar index percentage
                    const idxLeft = ((hovered.index + 0.5) * (100 / Math.max(1, data.length))) || 0;
                    leftStyle = { left: `${idxLeft.toFixed(2)}%`, transform: [{ translateX: -0.5 * (tooltipWidth || 0) }] };
                  }
                  const top = tooltipPos ? Math.max((tooltipPos.y || 0) - 36, 4) : 6;
                  return [styles.tooltip, { top }, leftStyle];
                })()}
              >
                <Text style={styles.tooltipText}>{tooltipText}</Text>
              </View>
            )}

            <View style={styles.barsRow}>
              {data.map((item, idx) => {
                const value = chartType === 'stacked' ? item.segments?.reduce((a, b) => a + b.value, 0) ?? 0 : item.value ?? 0;
                const height = Math.max((value / maxValue) * 100, 8);
                const showTextInside = height > 30;
                return (
                  <Pressable
                    key={item.label}
                    style={styles.col}
                    onPress={() => onBarPress?.(item)}
                    {...(webHoverHandlers(idx, item) as object)}
                    onPressIn={(e: any) => handlePressIn(idx, item, e)}
                    onLongPress={(e: any) => handlePressIn(idx, item, e)}
                  >
                    {(metadataPosition === 'top' || (metadataPosition === 'inside' && !showTextInside)) && (
                      <Text style={[styles.topValue, isDark && { color: colors.text.primary }]}>{item.topLabel ?? item.valueLabel}</Text>
                    )}

                    {metadataPosition === 'inside' && !showTextInside && item.percentage != null && (
                      <Text style={[styles.topValue, isDark && { color: colors.text.primary }]}>{item.percentage.toFixed(1)}%</Text>
                    )}

                    {chartType === 'single' ? (
                      <View style={[styles.bar, { height: `${height}%` }]}>
                        {metadataPosition === 'inside' && showTextInside && (
                          <>
                            {item.valueLabel && <Text style={styles.insideText}>{item.valueLabel}</Text>}
                            {item.percentage != null && <Text style={styles.insideText}>{item.percentage.toFixed(1)}%</Text>}
                          </>
                        )}
                      </View>
                    ) : (
                      <View style={[styles.stack, { height: `${height}%` }]}> 
                        {item.segments?.map(segment => (
                          <View
                            key={segment.label}
                            style={{ height: `${(segment.value / value) * 100}%`, backgroundColor: segment.color, justifyContent: 'center', alignItems: 'center' }}
                            {...(webHoverHandlers(idx, item, segment) as any)}
                            onStartShouldSetResponder={() => true}
                            // Native press handler for mobile
                            onTouchStart={(e: any) => handleSegmentPressIn(idx, item, segment, e)}
                          >
                            {segment.value > 0 && <Text style={styles.segmentText}>{segment.value}</Text>}
                          </View>
                        ))}
                      </View>
                    )}
                  </Pressable>
                );
              })}
            </View>

            <View style={styles.labelsRow}>
              {data.map(item => (
                <View key={item.label} style={styles.col}>
                  <Text style={[styles.label, isDark && { color: colors.text.secondary }]} numberOfLines={2} ellipsizeMode="tail">{item.label}</Text>

                  {metadataPosition === 'bottom' && (
                    <>
                      <Text style={styles.bottomValue}>{item.valueLabel}</Text>
                      {!!item.percentage && <Text style={styles.percentage}>{item.percentage.toFixed(1)}%</Text>}
                    </>
                  )}
                </View>
              ))}
            </View>
          </>
        )}
      </View>
      )}
    </View>
  );
};


const styles = StyleSheet.create({
  card: {
    backgroundColor: '#FFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E8EDF3',
    padding: 20,
    marginBottom: 16,
    overflow: 'hidden',
  },

  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 16,
  },

  leftHeader: {
    flexDirection: 'row',
    gap: 8,
  },

  title: {
    fontSize: 16,
    fontFamily: FontFamily.bold,
  },

  subtitle: {
    fontSize: 12,
    color: '#7A8088',
    marginTop: 4,
  },

  chart: {
    flexDirection: 'column',
    gap: 8,
    paddingHorizontal: 4,
  },

  col: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'flex-end',
    height: '100%',
  },

  barsRow: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 0,
    height: '100%',
  },

  labelsRow: {
    flexDirection: 'row',
    gap: 8,
    paddingTop: 2,
    alignItems: 'flex-start',
  },

  bar: {
    width: '100%',
    maxWidth: 72,
    backgroundColor: '#005CBC',
    borderRadius: 4,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 6,
  },

  stack: {
    width: '100%',
    maxWidth: 60,
    borderRadius: 4,
    overflow: 'hidden',
  },

  label: {
    marginTop: 2,
    textAlign: 'center',
    fontSize: 11,
    color: '#6A6D71',
    fontFamily: FontFamily.regular,
    fontWeight: '500',
    lineHeight: 14,
    flexShrink: 1,
    minWidth: 0,
    marginHorizontal: 2,
  },

  topValue: {
    marginBottom: 6,
    fontSize: 12,
    fontFamily: FontFamily.bold,
    color: '#2D2F33',
    textAlign: 'center',
  },

  bottomValue: {
    marginTop: 4,
  },

  percentage: {
    fontSize: 11,
  },

  insideText: {
    color: '#FFF',
    fontSize: 14,
    fontFamily: FontFamily.bold,
    fontWeight: '700',
  },

  toggle: {
    flexDirection: 'row',
    backgroundColor: '#F0F2F5',
    borderRadius: 8,
    padding: 3,
    alignSelf: 'flex-start',
  },

  toggleOption: {
    paddingHorizontal: 14,
    paddingVertical: 5,
    borderRadius: 6,
  },

  toggleOptionActive: {
    backgroundColor: '#FFFFFF',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08,
    shadowRadius: 2,
    elevation: 2,
  },

  toggleOptionText: {
    fontSize: 13,
    fontFamily: FontFamily.medium,
    color: '#6B7280',
  },

  toggleOptionTextActive: {
    color: '#111827',
    fontFamily: FontFamily.bold,
  },

  legendContainer: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 8,
  },

  legendContainerTopRight: {
    flexDirection: 'row',
    gap: 12,
  },

  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },

  legendDot: {
    width: 12,
    height: 12,
    borderRadius: 3,
  },

  backButton: {
    width: 28,
    height: 28,
    borderRadius: 14,
    borderWidth: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },

  segmentText: {
    color: '#FFF',
    fontSize: 11,
    fontFamily: FontFamily.bold,
  },

  tooltip: {
    position: 'absolute',
    top: 6,
    transform: [{ translateX: -50 }],
    backgroundColor: 'rgba(0,0,0,0.85)',
    paddingHorizontal: 8,
    paddingVertical: 6,
    borderRadius: 6,
    zIndex: 800,
  },

  tooltipText: {
    color: '#FFF',
    fontSize: 12,
    fontFamily: FontFamily.medium,
  },

  noDataWrap: {
    flex: 1,
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
  },

  noDataText: {
    color: '#7A8088',
    fontFamily: FontFamily.medium,
    fontSize: 14,
    textAlign: 'center',
  },
  errorText: {
    color: '#B42318',
    fontSize: 13,
    fontFamily: FontFamily.medium,
    textAlign: 'center',
  },
  retryButton: {
    marginTop: 12,
    paddingVertical: 6,
    paddingHorizontal: 16,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#1D4ED8',
    alignSelf: 'center',
  },
  retryText: {
    color: '#1D4ED8',
    fontSize: 13,
    fontFamily: FontFamily.medium,
    fontWeight: '600',
  },

  // ─── Skeleton ─────────────────────────────────────────────────────────────
  skeletonBar: {
    width: '100%',
    maxWidth: 72,
    backgroundColor: '#E5E7EB',
    borderTopLeftRadius: 4,
    borderTopRightRadius: 4,
  },

  skeletonLabel: {
    marginTop: 8,
    height: 8,
    width: '70%',
    maxWidth: 72,
    borderRadius: 4,
    backgroundColor: '#E5E7EB',
  },
});

export default VerticalBarChartCard;
